/* Levi Complete Scope Orchestration · advisory card.

   Reads the scope-orchestrator manifest for the current estimate and
   renders each recognized scope with per-scope actions. No scope
   silently disappears — every recognized scope must reach one of four
   end states (priced / needs_review / trade_not_setup / unresolved).

   The card never mutates the estimate on render. Only the explicit
   contractor actions (Set Up Trade / Price This Job Only / Skip For
   Now) trigger any change.
*/
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, HelpCircle, Wrench, ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LangContext";

const STATE_META = {
  priced:                 { icon: CheckCircle2,  tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  needs_review:           { icon: HelpCircle,    tone: "text-amber-900 bg-amber-50 border-amber-200" },
  trade_not_setup:        { icon: Wrench,        tone: "text-blue-900 bg-blue-50 border-blue-200" },
  unsupported_capability: { icon: HelpCircle,    tone: "text-slate-900 bg-slate-50 border-slate-200" },
  unresolved:             { icon: AlertTriangle, tone: "text-red-900 bg-red-50 border-red-200" },
};

// The trades that Trade Setup currently supports writable operations
// for. If a recognized scope's trade is here, "Set Up {Trade}" links
// to the wizard. Otherwise the "Price This Job Only" path is the
// contractor's only forward option.
const SETUP_SUPPORTED = new Set(["Roofing","Flooring","Painting","Drywall","Siding","Insulation"]);

// PO 2026-02 · Scope Check "Needs Review" actionability CTA. Scrolls
// the offending estimate line into view, briefly highlights it, and
// focuses the first editable control if the DOM exposes one. Does not
// mutate the estimate — presentation-only.
const _scrollToEstimateLine = (lineId) => {
  if (!lineId) return;
  const el = document.getElementById(`estimate-line-${lineId}`);
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  } catch (_e) {
    el.scrollIntoView();
  }
  el.classList.add("scope-review-highlight");
  window.setTimeout(() => {
    try { el.classList.remove("scope-review-highlight"); } catch (_e) {}
  }, 2400);
  const focusable = el.querySelector(
    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])'
  );
  if (focusable && typeof focusable.focus === "function") {
    try { focusable.focus({ preventScroll: true }); } catch (_e) { focusable.focus(); }
  }
};

export const LeviScopeOrchestrator = ({ estimateId, projectId, onLineItemAdded }) => {
  const { t } = useLang();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [busyKey, setBusyKey]   = useState(null);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/estimates/${estimateId}/scope-orchestrator`);
      setManifest(data);
    } catch (e) {
      setError(t("scope_orchestrator.load_failed"));
    } finally { setLoading(false); }
  }, [estimateId, t]);

  useEffect(() => { load(); }, [load]);

  const priceThisJobOnly = useCallback(async (scope) => {
    setBusyKey(scope.scope_key); setError(null);
    try {
      const { data } = await api.post(
        `/estimates/${estimateId}/scope-orchestrator/price-this-job-only`,
        { trade: scope.trade, source_field: scope.source_field, evidence: scope.evidence },
      );
      onLineItemAdded?.(data);
      await load();
    } catch (e) {
      setError(t("scope_orchestrator.action_failed"));
    } finally { setBusyKey(null); }
  }, [estimateId, load, onLineItemAdded, t]);

  const dismissScope = useCallback(async (scope) => {
    setBusyKey(scope.scope_key); setError(null);
    try {
      await api.post(
        `/estimates/${estimateId}/scope-orchestrator/dismiss`,
        { trade: scope.trade, source_field: scope.source_field },
      );
      await load();
    } catch (e) {
      setError(t("scope_orchestrator.action_failed"));
    } finally { setBusyKey(null); }
  }, [estimateId, load, t]);

  if (loading || !manifest) return null;
  const scopes = manifest.scopes || [];
  if (scopes.length === 0) return null;

  // Never lose scope: if EVERY scope is priced, show a compact "all
  // recognized scope accounted for" chip. Otherwise render the
  // per-scope advisory list.
  const nonPriced = scopes.filter((s) => s.state !== "priced");
  const allPriced = nonPriced.length === 0;

  return (
    <section
      className="jc-card p-5 mb-6"
      data-testid="levi-scope-orchestrator"
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline">{t("scope_orchestrator.overline")}</div>
          <h3 className="font-display text-base mt-0.5" data-testid="levi-scope-orchestrator-title">
            {allPriced
              ? t("scope_orchestrator.all_priced_title")
              : t("scope_orchestrator.headline")}
          </h3>
          <p className="text-xs text-[var(--jc-text-soft)] mt-0.5">
            {t("scope_orchestrator.subhead")}
          </p>
        </div>
        <div className="text-xs text-right shrink-0" data-testid="levi-scope-orchestrator-summary">
          <span className="inline-flex items-center gap-1 text-emerald-800">
            <CheckCircle2 size={12} /> {t("scope_orchestrator.count_priced", manifest.summary.priced)}
          </span>
          {manifest.summary.needs_review > 0 && (
            <span className="inline-flex items-center gap-1 ml-2 text-amber-800">
              <HelpCircle size={12} /> {t("scope_orchestrator.count_needs_review", manifest.summary.needs_review)}
            </span>
          )}
          {manifest.summary.trade_not_setup > 0 && (
            <span className="inline-flex items-center gap-1 ml-2 text-blue-800">
              <Wrench size={12} /> {t("scope_orchestrator.count_trade_not_setup", manifest.summary.trade_not_setup)}
            </span>
          )}
          {(manifest.summary.unsupported_capability || 0) > 0 && (
            <span className="inline-flex items-center gap-1 ml-2 text-slate-800" data-testid="levi-scope-orchestrator-count-manual">
              <HelpCircle size={12} /> {t("scope_orchestrator.count_unsupported_capability", manifest.summary.unsupported_capability)}
            </span>
          )}
          {manifest.summary.unresolved > 0 && (
            <span className="inline-flex items-center gap-1 ml-2 text-red-800">
              <AlertTriangle size={12} /> {t("scope_orchestrator.count_unresolved", manifest.summary.unresolved)}
            </span>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800"
             data-testid="levi-scope-orchestrator-error">
          {error}
        </div>
      )}

      <ul className="space-y-2" data-testid="levi-scope-orchestrator-list">
        {scopes.map((scope) => {
          const meta = STATE_META[scope.state] || STATE_META.needs_review;
          const Icon = meta.icon;
          const supported = SETUP_SUPPORTED.has(scope.trade);
          const busy = busyKey === scope.scope_key;
          return (
            <li
              key={scope.scope_key}
              data-testid={`levi-scope-item-${scope.scope_key}`}
              className={`rounded-md border p-3 ${meta.tone}`}
            >
              <div className="flex items-start gap-2">
                <Icon size={16} className="shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-sm">{scope.trade}</span>
                    <span className="text-[10px] uppercase tracking-wider opacity-80"
                          data-testid={`levi-scope-state-${scope.scope_key}`}>
                      {t(`scope_orchestrator.state.${scope.state}`)}
                    </span>
                  </div>
                  {/* Consolidated evidence — one row per unique source
                      field, with friendly localized labels. */}
                  {Array.isArray(scope.evidence_sources) && scope.evidence_sources.length > 0 && (
                    <div className="text-xs opacity-90 mt-0.5"
                         data-testid={`levi-scope-evidence-${scope.scope_key}`}>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">
                        {t("scope_orchestrator.recognized_from")}
                      </div>
                      <ul className="mt-0.5 space-y-0.5">
                        {scope.evidence_sources.map((e) => (
                          <li key={e.source_field}
                              data-testid={`levi-scope-evidence-src-${scope.scope_key}-${e.source_field}`}
                              className="text-xs">
                            <span className="font-semibold">
                              {t(`scope_orchestrator.source_field.${e.source_field}`)}
                            </span>
                            {e.evidence ? <span className="italic opacity-80"> — “{e.evidence}”</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Per-state guidance sentence (contractor-facing, no
                      internal state names). */}
                  {(scope.state === "trade_not_setup" || scope.state === "unsupported_capability") && (
                    <div className="text-xs opacity-90 mt-1.5" data-testid={`levi-scope-guidance-${scope.scope_key}`}>
                      {scope.state === "trade_not_setup"
                        ? t("scope_orchestrator.guidance.trade_not_setup", scope.trade)
                        : t("scope_orchestrator.guidance.unsupported_capability", scope.trade)}
                    </div>
                  )}
                  {/* Per-state actions */}
                  {(scope.state === "trade_not_setup" || scope.state === "unsupported_capability" || scope.state === "unresolved") && (
                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                      {scope.state === "trade_not_setup" && supported && (
                        <Link
                          to={`/app/onboarding/trade-setup/${encodeURIComponent(scope.trade)}`}
                          data-testid={`levi-scope-setup-${scope.scope_key}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
                        >
                          <Wrench size={12} /> {t("scope_orchestrator.set_up_trade", scope.trade)}
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => priceThisJobOnly(scope)}
                        disabled={busy}
                        data-testid={`levi-scope-price-job-${scope.scope_key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border)] bg-white text-[var(--jc-text)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
                      >
                        {t("scope_orchestrator.price_this_job_only")}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissScope(scope)}
                        disabled={busy}
                        data-testid={`levi-scope-dismiss-${scope.scope_key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] disabled:opacity-60"
                      >
                        {t("scope_orchestrator.skip_for_now")}
                      </button>
                    </div>
                  )}
                  {scope.state === "needs_review" && (
                    <div className="mt-1.5 space-y-2">
                      <div className="text-xs opacity-80">
                        {t("scope_orchestrator.needs_review_hint")}
                      </div>
                      {Array.isArray(scope.review_line_ids) && scope.review_line_ids.length > 0 && (
                        <button
                          type="button"
                          onClick={() => _scrollToEstimateLine(scope.review_line_ids[0])}
                          data-testid={`levi-scope-review-${scope.scope_key}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                        >
                          <ArrowRight size={12} /> {t("scope_orchestrator.review_line")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {scope.state === "priced" && (
                  <ArrowRight size={12} className="opacity-40 mt-1 shrink-0" />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default LeviScopeOrchestrator;
